import { Dispatch, SetStateAction } from 'react';
/**
 * @desc 用途：自定义表格组件的接口定义
 */
type ElementInfo = React.ReactNode | string | JSX.Element | number;
/**
 * @desc 元素icon类型
 */
enum ElementIconType {
    ICON = 'iconfont', // iconfont http://design.umu.work/components/iconfont
    CLS = 'cls', // className 类名
    IMG = 'img', // 图片
}
/**
 * @desc 元素类型 （主要为PC angular 提供react 元素组件）
 */
enum ElementType {
    __INIT__ = '', // 初始化 <div />
    PROGRESS = 'progress', // 进度条
    USER_LABEL = 'userLabel', // 用户标签
    STATUS_LABEL = 'statusLabel', // 状态标签
    STATUS_LABEL_LIST = 'statusLabelList', // 状态标签列表
    BUTTON = 'button', // 按钮
    SWITCH = 'switch', // 开关
    USER_POINT_DETAIL = 'userSessionPointDetail', // 用户会话积分详情
    BUTTON_LIST = 'buttonList', // 按钮列表
    LABEL_ICON = 'labelIcon', // 标签icon
}
interface IFooterBtnInfo extends React.HTMLAttributes<HTMLButtonElement> {
    className?: string;
    label: JSX.Element | string;
    onClick: () => void;
}
interface ITitleIcon extends React.HTMLAttributes<HTMLDivElement> {
    type: ElementIconType;
    iconType?: string;
}
interface IComponentConfigInfo {
    type: ElementType;
    config?: any;
}
type IContentInfo = ElementInfo | IComponentConfigInfo;
interface ITipConfig {
    isShow?: boolean;
    titleContent?: ElementInfo | IComponentConfigInfo;
    showTitle?: boolean; // 是否展示当前title
    content?: IContentInfo | IContentInfo[];
    footerContent?: {
        btnList: IFooterBtnInfo[];
    };
    getConfigInfo?: (params: { value: any; info: any }) => any;
    config?: any;
}
/**
 * @desc
 */
interface ISelectionsCheckInfo<T> {
    readonly selected: T[];
    readonly noneSelected: boolean;
    readonly allSelected: boolean;
    readonly partiallySelected: boolean;
    readonly setSelected: Dispatch<SetStateAction<T[]>>;
    readonly isSelected: (item: T) => boolean;
    readonly select: (item: T) => void;
    readonly unSelect: (item: T) => void;
    readonly toggle: (item: T) => void;
    readonly selectAll: () => void;
    readonly unSelectAll: () => void;
    readonly toggleAll: () => void;
}
interface IColumnsExpandInfo {
    checkInfo: ISelectionsCheckInfo<string>;
    checkKey: string;
}
interface ICustomAdvancedTableColumnTitle extends React.HTMLAttributes<HTMLDivElement> {
    title: string;
    align?: string;
    checkbox?: boolean;
    isCustomTitle?: boolean;
    titlePrefix?: ElementInfo;
    titleIcon?: ITitleIcon;
    suffixTitleIcon?: ITitleIcon;
    titleSuffix?: ElementInfo;
    tooltipConfig?: ITipConfig;
    expandInfo?: IColumnsExpandInfo;
}

interface ICustomAdvancedTableRenderConfig {
    type: ElementType;
    className?: string;
    config?: Record<string, any>;
    tooltipConfig?: ITipConfig;
    onClick?: (value: any) => void;
}

interface ICustomAdvancedTableColumns<T> extends ICustomAdvancedTableColumnTitle {
    key: string; // 列的key
    width?: number | string; // 列的宽度
    autoWidth?: boolean; // 是否自适应宽度(目前只支持一列)
    widthClassName?: string; // 列的宽度类名
    fixed?: string | boolean; // 是否固定列
    render?: (text: T[keyof T] | string, record: T, index?: number) => ElementInfo; // 自定义渲染
    tooltipConfig?: ITipConfig; // 提示配置
    renderConfig?: ICustomAdvancedTableRenderConfig; // 自定义渲染配置
}
/**
 * @desc 表格配置信息 用于自定义表格组件
 */
interface IPropsTable<T> extends React.HTMLAttributes<HTMLDivElement> {
    isLoading?: boolean; // 是否加载中
    sticky?: boolean; // 是否固定表头
    checkKey?: string; // 勾选数据的path 可以写为 'a.b.c'
    offsetX?: number; // 滚动条偏移量
    isVirtualScroll?: boolean; // 是否开启虚拟滚动
    emptyText?: ElementInfo; // 空数据时的提示
    skeletonInfo?: {
        // 骨架屏配置
        /** 要显示的列数 */
        columns: number;
        /** 要显示的行数 */
        count: number;
        /** 是否展示条纹动画 */
        showStripes?: boolean;
        /** 单元格高度 */
        cellHeight?: number | string;
    };
    list: T[]; // 表格数据
    scroll?: {
        x?: number | true | string;
        y?: number | string;
    }; // 滚动配置
    columns: ICustomAdvancedTableColumns<T>[];
    pageInfo?: IPageInfo; // 分页信息
    mountElementCls?: string; // 挂载元素的类名
    tableCls?: string; // 表格的类名
    rowClassName?: (record: T, index: number) => string; // 行的类名
    onPageChange: (page: number) => void; // 分页回调
    onCheckChange?: (checkList: string[]) => void; // 勾选回调
}

export {
    IPropsTable,
    ElementIconType,
    ITitleIcon,
    ITipConfig,
    ElementType,
    ElementInfo,
    IContentInfo,
    IColumnsExpandInfo,
    IFooterBtnInfo,
    IComponentConfigInfo,
    ISelectionsCheckInfo,
    ICustomAdvancedTableRenderConfig,
    ICustomAdvancedTableColumnTitle,
    ICustomAdvancedTableColumns,
};
